"use client";

import { Button } from "@repo/ui/components/ui/button";
import { Calendar } from "@repo/ui/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { Field, FieldDescription, FieldError, FieldLabel } from "@repo/ui/form-fields";
import { useFieldContext } from "@/shared/forms/form-context";

export interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

export interface DateRangeFieldProps {
  label?: string;
  description?: string;
  placeholder?: string;
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
  disabled?: boolean;
  numberOfMonths?: number;
}

export function DateRangeField({
  label,
  description,
  placeholder = "Select date range",
  captionLayout,
  disabled,
  numberOfMonths = 2,
}: DateRangeFieldProps) {
  const field = useFieldContext<DateRangeValue>();

  const formatRange = () => {
    const { from, to } = field.state.value ?? { from: null, to: null };
    if (!from) return placeholder;
    if (!to) return from.toLocaleDateString();
    return `${from.toLocaleDateString()} - ${to.toLocaleDateString()}`;
  };

  return (
    <Field errors={field.state.meta.errors} isTouched={field.state.meta.isTouched}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      <Popover>
        <PopoverTrigger
          render={
            <Button
              variant="outline"
              className={`w-full justify-start font-normal ${label ? "mt-1" : ""}`}
              disabled={disabled}
            >
              {formatRange()}
            </Button>
          }
        />
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="range"
            selected={
              field.state.value?.from
                ? { from: field.state.value.from, to: field.state.value.to ?? undefined }
                : undefined
            }
            defaultMonth={field.state.value?.from ?? undefined}
            captionLayout={captionLayout}
            numberOfMonths={numberOfMonths}
            onSelect={(range) => {
              field.handleChange({
                from: range?.from ?? null,
                to: range?.to ?? null,
              });
              if (range?.to) field.handleBlur();
            }}
          />
        </PopoverContent>
      </Popover>
      <FieldError />
    </Field>
  );
}
